import { Box, RadioProps, useRadio, UseRadioProps } from "@chakra-ui/react";
import useBreakpoint from "@/hooks/use-breakpoint";
import React, { useLayoutEffect, useRef } from "react";

// SelectBox.tsx

export interface SelectBoxProps extends UseRadioProps {
   isDisabled?: boolean;
   children: React.ReactNode;
}

export const SelectBox = React.forwardRef<HTMLLabelElement, SelectBoxProps>(
   ({ children, isDisabled = false, ...radioProps }, ref) => {
      const { getInputProps, getCheckboxProps } = useRadio(radioProps);
      const { isMobile } = useBreakpoint();

      const inputProps = getInputProps({ isDisabled });
      const cardProps = getCheckboxProps();

      return (
         <Box
            as="label"
            ref={ref}
            cursor={isDisabled ? "not-allowed" : "pointer"}
            display="inline-block"
            width={{ base: "100%", md: "439px" }} // ← fluid on mobile, fixed on md+
         >
            <input
               {...inputProps}
               hidden={true}
               disabled={isDisabled}
               tabIndex={-1}
               style={{ pointerEvents: "none" }}
            />

            <Box
               {...cardProps}
               display="flex"
               flexDirection="column"
               justifyContent="center"
               alignItems="flex-start"
               p={isMobile ? "12px 16px" : "16px 20px"}
               gap="4px"
               minH="60px" // ← ensure at least 60px tall
               bg={isDisabled ? "#F5F5F6" : "#F5F5F5"}
               borderRadius="12px"
               transition="all 0.2s ease"
               opacity={isDisabled ? 0.6 : 1}
               _hover={
                  isDisabled
                     ? {}
                     : {
                          borderColor: "#1E345533",
                          borderWidth: "2px",
                          bg: "#1E34551A",
                       }
               }
               _checked={
                  isDisabled
                     ? {}
                     : {
                          fontWeight: "bold",
                          borderColor: "#88B6E0",
                          borderWidth: "2px",
                          bg: "#FFFFFF",
                       }
               }
            >
               <Box display="grid" gridTemplateColumns="1fr auto" w="100%">
                  {children}
               </Box>
            </Box>
         </Box>
      );
   }
);
SelectBox.displayName = "SelectBox";

export const SelectColorBox = ({ children, ...rest }: RadioProps) => {
   const { getInputProps, getRadioProps } = useRadio(rest);
   const { isMobile } = useBreakpoint();
   const inputRef = useRef<HTMLInputElement>(null);

   useLayoutEffect(() => {
      const container = document.querySelector("#scroll-container");
      if (container) {
         const onScroll = () => console.log("scrolling...");
         container.addEventListener("scroll", onScroll);
         return () => container.removeEventListener("scroll", onScroll);
      }
      return;
   }, []);

   const input = getInputProps();
   const radio = getRadioProps();

   return (
      <Box as="label" display="block" id="scroll-container">
         <Box
            {...radio}
            cursor="pointer"
            borderWidth="1px"
            borderRadius="md"
            borderColor="gray.300"
            py="18px"
            px={isMobile ? "16px" : "24px"}
            fontSize={isMobile ? "1rem" : "1.25rem"}
            fontWeight={400}
            lineHeight="1.2em"
            color="gray.800"
            backgroundColor="white"
            transition="all 0.2s ease"
            _hover={{
               borderColor: "brandBlue.300",
               backgroundColor: "gray.50",
            }}
            _focus={{
               boxShadow: "0 0 0 2px #4C91BB33",
            }}
            _checked={{
               fontWeight: 700,
               borderColor: "brandBlue.400",
               backgroundColor: "brandBlue.50",
            }}
         >
            {children}
         </Box>
         <input ref={inputRef} {...input} style={{ border: "1px solid red" }} />
      </Box>
   );
};
